'use client'
import Header from 'components/Header'
import navData from 'components/Navigation/navigation.json'
import { mainProductConfig } from 'constants/mainProductConfig'
import { FEATURE_TOGGLES, VARIATIONS_UPPER } from 'constants/featureToggles'
import { validateHeaderItem } from 'utils/validations'
import classNames from 'classnames'
import HeaderMobileLeft from 'components/Header/HeaderMobileLeft/HeaderMobileLeft'
import FDRPersistentHeader from 'components/Header/FDRPersistentHeader'
import { BillsPage, FdrPage } from 'utils/environmentConfig'
import BillsHeader from 'components/Header/BillsHeader/BillsHeader'
import { MegaMenu } from 'components/MegaMenu/MegaMenu'
import useLayoutMenuCondensed from '@/hooks/useLayoutMenuCondensed'
import useMobileNavState from 'hooks/useMobileNavState'
import { getFeatureFlag } from '@/utils/featureFlags'

export default function HeaderContainer({
  content = {},
  siteEnv = 'achieve',
  isMobileUA = false,
  customPhoneData = null,
  phoneData,
  disabledElements,
  disabledRoutes,
  showProgressPage,
  specificHeaderCtaDataFiltered,
  footerSticky,
  showPhoneNumber,
  menuMobileLeft,
  dynamicStickyNav,
  isHomePath,
}) {
  const [menuCondensed] = useLayoutMenuCondensed()
  const [mobileNavOpen] = useMobileNavState()
  const {
    headerCta = mainProductConfig[siteEnv || 'achieve']?.['headerCta'],
    link = {},
    showNav = true,
    showPhone = mainProductConfig[siteEnv || 'achieve']?.['showPhone'],
    showSignIn = mainProductConfig[siteEnv || 'achieve']?.['showSignIn'],
    signIn = {},
    showDebtConsultantCta,
    stickyHeaderSettings = {},
  } = content
  let linkEvent = link?.event
  let signInEvent = signIn?.event
  const mapContentfulToHeader = (data = []) => {
    if (
      !data ||
      (Object.keys(data).length === 0 && data.constructor === Object) ||
      data.length === 0
    ) {
      return navData?.[siteEnv]?.mainNavigation || navData?.achieve?.mainNavigation
    }
    return validateHeaderItem(data).map((navItem) => {
      if (navItem?.fields?.uiComponent === 'Grid') {
        return {
          label: navItem.fields.name,
          subNavMenus: [
            {
              menuTitle: '',
              menuItems: validateHeaderItem(navItem.fields.gridItems).map((subNav) => {
                // Check if this sub-item has nested gridItems (third level)
                const hasNestedItems =
                  subNav?.fields?.uiComponent === 'Grid' && subNav?.fields?.gridItems

                return {
                  label: subNav.fields?.name,
                  description: subNav?.fields?.linkText?.content?.[0].content?.[0]?.value,
                  testid: subNav?.fields?.linkText?.content?.[0].content?.[0]?.value,
                  url: subNav.fields?.linkHref,
                  // Add nested sub-items if they exist
                  ...(hasNestedItems && {
                    subItems: validateHeaderItem(subNav.fields.gridItems).map((nestedItem) => ({
                      label: nestedItem.fields?.name,
                      url: nestedItem.fields?.linkHref,
                      testid: nestedItem.fields?.name,
                    })),
                  }),
                }
              }),
            },
          ],
          testid: navItem.fields.name,
        }
      } else {
        return {
          label: navItem.fields.name,
          url: navItem.fields.linkHref,
          testid: navItem?.fields?.name,
        }
      }
    })
  }

  const isBills = siteEnv === BillsPage
  const isFDR = siteEnv === FdrPage
  const enableMegaMenu =
    getFeatureFlag(FEATURE_TOGGLES[siteEnv]?.ACX_WEB_ENABLE_MEGA_MENU) ===
    VARIATIONS_UPPER.VARIATION_A

  const mainNavigation = mapContentfulToHeader(content.navItems)
  const mainNavigationMobile = mapContentfulToHeader(content.navItemsMobile)

  const dataHeader = {
    disabledElements,
    disabledRoutes,
    mainNavigation,
    mainNavigationMobile,
    signInLinkUrl: signIn.fields?.linkHref,
    signInLinkText: signIn.fields?.linkText?.content[0]?.content[0]?.value,
    signInEvent,
    linkUrl: specificHeaderCtaDataFiltered ?? link.fields?.linkHref,
    linkText: link.fields?.linkText?.content[0]?.content[0]?.value,
    linkEvent,
    headerCta,
    showNav,
    showSignIn,
    customPhoneData,
    logoLink: content.logoLink ?? null,
    partnerLogo: content.partnerLogo?.fields ?? null,
    showPhone,
    showProgressPage,
    siteEnv,
    footerSticky,
    showPhoneNumber,
    phoneData,
    showDebtConsultantCta,
    stickyHeaderSettings,
    dynamicStickyNav,
    isHomePath,
    isMobileUA,
  }

  if (isBills) {
    return (
      <div data-testid="header-bills" className="overflow-hidden">
        <BillsHeader {...dataHeader} />
      </div>
    )
  }

  if (isFDR && enableMegaMenu && content?.navigation) {
    return <MegaMenu siteEnv={siteEnv} content={content} {...dataHeader} />
  }

  return (
    <div>
      {/* FDR hides it while condensed instead of unmounting it, so its nav keeps the measured
          collapsed sub menus when opened from the persistent header */}
      {menuMobileLeft && (
        <div
          data-testid="header-left"
          className={classNames('lg:hidden', {
            hidden: isFDR && menuCondensed && !mobileNavOpen,
          })}
        >
          <HeaderMobileLeft {...dataHeader} />
        </div>
      )}
      <div
        data-testid="header"
        className={classNames('hidden lg:block', { 'block!': !menuMobileLeft })}
      >
        <Header {...dataHeader} />
      </div>
      {isFDR && (
        <FDRPersistentHeader
          siteEnv={siteEnv}
          footerSticky={footerSticky}
          linkUrl={dynamicStickyNav?.link?.fields?.linkHref ?? dataHeader.linkUrl}
          phoneData={phoneData}
          slideWithMobileNav={Boolean(menuMobileLeft)}
        />
      )}
    </div>
  )
}
